/**
 * Minimal EBML parser that extracts the `Duration` element out of a
 * WebM / Matroska SegmentInfo without involving a <video> element.
 *
 * Why: Safari (as of 18.4) doesn't expose `<video>.duration` for WebM
 * produced by `webm-muxer` even though the Duration element is present
 * in the file's SegmentInfo. Chrome reads it fine. To keep Safari's
 * file-upload flow usable we parse the EBML ourselves.
 *
 * The result is in seconds. Returns null when the Duration element
 * cannot be located in the first chunk of the file.
 *
 * Note: only the Duration and TimestampScale elements in SegmentInfo
 * are consulted. Everything else in the EBML tree is skipped.
 */

const ID_SEGMENT_INFO = 0x1549a966;
const ID_DURATION = 0x4489;
const ID_TIMESTAMP_SCALE = 0x2ad7b1;

/**
 * Parse the first `scanBytes` of a WebM blob to extract Duration (in
 * seconds) from SegmentInfo. SegmentInfo is normally written to the
 * top of the file (ahead of Cluster data), so 256 KB is ample.
 */
export async function readWebMDuration(blob: Blob, scanBytes = 256 * 1024): Promise<number | null> {
  const slice = blob.slice(0, Math.min(blob.size, scanBytes));
  const buf = new Uint8Array(await slice.arrayBuffer());
  return scanBufferForDuration(buf);
}

function scanBufferForDuration(buf: Uint8Array): number | null {
  // First, locate the SegmentInfo element. Look for its 4-byte ID.
  const infoOff = findMatroskaElement(buf, ID_SEGMENT_INFO, 4);
  if (infoOff < 0) return null;

  // After the ID comes the size (VInt); skip past it.
  const { size: infoSize, next: infoBodyStart } = readVInt(buf, infoOff + 4);
  if (infoSize < 0) return null;
  const infoBodyEnd = Math.min(buf.length, infoBodyStart + infoSize);

  // Walk children inside SegmentInfo. We only care about TimestampScale
  // and Duration.
  let timestampScale = 1_000_000; // Matroska default: 1 ms per tick
  let durationTicks: number | null = null;

  let p = infoBodyStart;
  while (p < infoBodyEnd) {
    const idRead = readVIntKeepMarker(buf, p);
    if (idRead.width < 0) break;
    const id = idRead.value;
    p = idRead.next;
    const sizeRead = readVInt(buf, p);
    if (sizeRead.size < 0) break;
    const bodyStart = sizeRead.next;
    const bodyEnd = bodyStart + sizeRead.size;
    if (bodyEnd > buf.length) break;

    if (id === ID_TIMESTAMP_SCALE) {
      timestampScale = readUint(buf, bodyStart, sizeRead.size);
    } else if (id === ID_DURATION) {
      durationTicks = readFloat(buf, bodyStart, sizeRead.size);
    }
    p = bodyEnd;
  }

  if (durationTicks === null || !Number.isFinite(durationTicks)) return null;
  // Duration is measured in TimestampScale ticks; the scale is nanoseconds.
  const durationSeconds = (durationTicks * timestampScale) / 1e9;
  return durationSeconds > 0 ? durationSeconds : null;
}

/**
 * Find the first occurrence of an EBML element with a known `idValue`
 * whose ID is `idBytes` wide. A simple linear memmem that accepts the
 * ID as a big-endian integer.
 */
function findMatroskaElement(buf: Uint8Array, idValue: number, idBytes: number): number {
  // Build the ID byte pattern.
  const pattern = new Uint8Array(idBytes);
  for (let i = 0; i < idBytes; i += 1) {
    pattern[i] = (idValue >>> ((idBytes - 1 - i) * 8)) & 0xff;
  }
  for (let i = 0; i <= buf.length - idBytes; i += 1) {
    let match = true;
    for (let j = 0; j < idBytes; j += 1) {
      if (buf[i + j] !== pattern[j]) {
        match = false;
        break;
      }
    }
    if (match) return i;
  }
  return -1;
}

/**
 * Read an EBML variable-length integer with the leading marker bits
 * stripped. Returns the decoded value and the byte offset of the next
 * element. Returns size=-1 if the input is truncated.
 */
function readVInt(buf: Uint8Array, offset: number): { size: number; next: number } {
  if (offset >= buf.length) return { size: -1, next: offset };
  const first = buf[offset];
  if (first === 0) return { size: -1, next: offset };
  // Count leading zeros to find width
  let width = 1;
  let mask = 0x80;
  while ((first & mask) === 0 && width < 8) {
    width += 1;
    mask >>>= 1;
  }
  if (offset + width > buf.length) return { size: -1, next: offset };
  let value = first & (mask - 1);
  for (let i = 1; i < width; i += 1) {
    value = value * 256 + buf[offset + i];
  }
  return { size: value, next: offset + width };
}

/**
 * Like readVInt but retains the width marker bits in the returned value.
 * Used for IDs (which are matched as raw byte sequences anyway) — we
 * include this for the SegmentInfo children walk.
 */
function readVIntKeepMarker(
  buf: Uint8Array,
  offset: number,
): { value: number; width: number; next: number } {
  if (offset >= buf.length) return { value: 0, width: -1, next: offset };
  const first = buf[offset];
  if (first === 0) return { value: 0, width: -1, next: offset };
  let width = 1;
  let mask = 0x80;
  while ((first & mask) === 0 && width < 8) {
    width += 1;
    mask >>>= 1;
  }
  if (offset + width > buf.length) return { value: 0, width: -1, next: offset };
  let value = first;
  for (let i = 1; i < width; i += 1) {
    value = value * 256 + buf[offset + i];
  }
  return { value, width, next: offset + width };
}

function readUint(buf: Uint8Array, offset: number, size: number): number {
  let v = 0;
  for (let i = 0; i < size; i += 1) v = v * 256 + buf[offset + i];
  return v;
}

function readFloat(buf: Uint8Array, offset: number, size: number): number {
  const dv = new DataView(buf.buffer, buf.byteOffset + offset, size);
  if (size === 4) return dv.getFloat32(0, false);
  if (size === 8) return dv.getFloat64(0, false);
  return Number.NaN;
}
